<template>
  <ui-modal-layout>
    <template #header>
      <social-page :source-profile="item.post.source_profile" :date="item.entity.created_at" />
    </template>

    <section class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ shortLabel }}</h3>
      <inline-social-content :entity="item.entity" />
    </section>

    <section v-if="whyNotText" class="flex flex-col gap-1.5">
      <h3 v-if="whyNotQuestion" class="m-0 text-sm font-semibold text-foreground">{{ whyNotQuestion }}</h3>
      <p class="m-0 text-sm text-muted-foreground">{{ whyNotText }}</p>
    </section>

    <section v-if="isTypeFilter || isMetricFilter" class="flex flex-col gap-3 rounded-lg border border-destructive/35 bg-destructive/5 p-4">
      <h3 class="m-0 flex items-center gap-2 text-sm font-semibold text-destructive">
        <circle-x-icon class="size-4 shrink-0" />
        {{ isTypeFilter ? filterInfo.filter_label : metricFilterHeaderLabel }}
      </h3>

      <template v-if="isTypeFilter">
        <p class="m-0 text-sm text-foreground">
          {{ tr(entityTypeLabel) }}: <strong>{{ filterInfo.entity_value }}</strong>
        </p>
        <p class="m-0 text-sm text-muted-foreground">
          {{ tr('Only the following types are allowed in the automation:') }}
          {{ filterInfo.allowed_values.join(', ') }}
        </p>
      </template>

      <template v-else>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex min-w-0 flex-col gap-0.5">
            <span class="text-xs font-semibold text-muted-foreground">{{ tr(entityValueLabel) }}</span>
            <span :class="[ 'font-bold text-foreground [overflow-wrap:anywhere]', typeof filterInfo.entity_value === 'string' && filterInfo.entity_value.length > 20 ? 'text-sm' : 'text-lg' ]">
              {{ filterInfo.entity_value }}
            </span>
          </div>
          <div class="flex min-w-0 flex-col gap-0.5">
            <span class="text-xs font-semibold text-muted-foreground">{{ tr('Requirement') }}</span>
            <span class="text-lg font-bold text-destructive">{{ filterInfo.requirement_value }}</span>
          </div>
        </div>
        <p class="m-0 text-sm text-muted-foreground">{{ filterInfo.explanation }}</p>
      </template>
    </section>

    <section v-if="item.isProhibitedContent" class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Reasoning of Infinite∞Ai') }}</h3>
      <p class="m-0 text-sm text-muted-foreground">{{ item.related_entity.reason_translation }}</p>
    </section>

    <section v-if="whatToDoText" class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('What can you do?') }}</h3>
      <p class="m-0 text-sm text-muted-foreground">{{ whatToDoText }}</p>
    </section>

    <section v-if="item.related_entity !== null" class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Status') }}</h3>
      <rule-waiting-list-status
        :rule="rule"
        :entity="item.related_entity"
        :disabled="$root.isSupportMode"
        in-modal
      />
    </section>
  </ui-modal-layout>
</template>

<script>
import { CircleX } from '@lucide/vue';

import { RULE_ACTION, RULE_REVIEW_FILTER_DEFINITIONS } from '@/consts.js';
import RuleActivityReviewItem from '@/descriptors/RuleActivityReviewItem.js';
import Rule from '@/entities/Rule.js';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import RuleWaitingListStatus from '@/ui/components/RuleWaitingListStatus.vue';
import SocialPage from '@/ui/components/SocialPage.vue';

export default {
  name: 'RuleActivityReview',
  components: {
    CircleXIcon: CircleX,
    InlineSocialContent,
    RuleWaitingListStatus,
    SocialPage,
  },
  props: {
    rule: {
      type: Rule,
      required: true,
    },
    item: {
      type: RuleActivityReviewItem,
      required: true,
    },
  },
  computed: {
    definition() {
      return RULE_REVIEW_FILTER_DEFINITIONS.find(item => item.code === this.item.code);
    },
    label() {
      return this.tr(this.definition.label[this.rule.action]);
    },
    shortLabel() {
      return this.tr(this.definition.shortLabel[this.rule.action]);
    },
    filterInfo() {
      return this.item.filter_info;
    },
    isTypeFilter() {
      return this.filterInfo && this.filterInfo.isTypeFilter;
    },
    isMetricFilter() {
      return this.filterInfo && this.filterInfo.isMetricFilter;
    },
    entityTypeLabel() {
      if (this.item.entity_type === 'ad') {
        return this.tr('The ad type');
      }

      return this.tr('The post type');
    },
    entityValueLabel() {
      if (this.item.entity_type === 'ad') {
        return this.tr('Ad value');
      }

      return this.tr('Post value');
    },
    metricFilterHeaderLabel() {
      return this.filterInfo.headerLabel;
    },
    whyNotQuestion() {
      switch (this.rule.action) {
        case RULE_ACTION.START:
          return this.tr('Why isn\'t an ad being created?');

        case RULE_ACTION.STOP:
          return this.tr('Why isn\'t this ad being stopped?');

        case RULE_ACTION.SCALING_UP:
          return this.tr('Why isn\'t this ad being scaled up?');
      }

      return null;
    },
    whyNotText() {
      if (this.definition.whyNot && this.definition.whyNot[this.rule.action]) {
        return this.tr(this.definition.whyNot[this.rule.action]);
      }

      return null;
    },
    whatToDoText() {
      if (
        this.item.isRejectedInActivityManagement
        && (!this.item.related_entity?.canBeAcceptedAfterRejection || this.$root.loggedInUser.isRestrictedViewer)
      ) {
        return null;
      }

      if (this.definition.whatToDo && this.definition.whatToDo[this.rule.action]) {
        return this.tr(this.definition.whatToDo[this.rule.action]);
      }

      return null;
    },
  },
};
</script>
